import React, { useEffect, useCallback, useState } from 'react'
import styled from 'styled-components'
import PreviewImagePNG from '../../../../assets/images/dummyImage.jpg'

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.2s ease-out;
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`

const MainContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 60px 80px;
  box-sizing: border-box;
`

const ImageWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  max-height: 100%;
  transition: opacity 0.2s ease;
  position: relative;
`

const ImageFrame = styled.div`
  position: relative;
  display: inline-flex;
  max-width: 100%;
  max-height: 100%;
`

const FullPageImage = styled.img`
  max-width: 100%;
  max-height: calc(100vh - 120px);
  object-fit: contain;
  border-radius: 8px;
  user-select: none;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`

const WatermarkOverlay = styled.img`
  position: absolute;
  bottom: 14px;
  right: 14px;
  width: 18%;
  min-width: 64px;
  max-width: 160px;
  max-height: 120px;
  object-fit: contain;

  pointer-events: none;
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  z-index: 15;
`

const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 24px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, transform 0.15s;
  z-index: 10;
  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: scale(1.05);
  }
  &:active {
    transform: scale(0.98);
  }
  span{
    padding-bottom:5px;
    }
`

const NavButton = styled.button<{ side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${(p) => (p.side === 'left' ? 'left: 20px;' : 'right: 20px;')}
  transform: translateY(-50%);
  width: 56px;
  height: 56px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, transform 0.15s;
  z-index: 10;
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-50%) scale(1.08);
  }
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    &:hover {
      transform: translateY(-50%);
    }
  }
    span{
    padding-bottom:5px;
    }
`

const Counter = styled.div`
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 20px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 600;
  border-radius: 24px;
  z-index: 10;
`

export interface CarouselImageItem {
  url: string
  thumbnail?: string
}

interface FullPageImageCarouselProps {
  images: CarouselImageItem[]
  initialIndex: number
  onClose: () => void
  activateWatermark?: boolean
  watermarkUrl?: string
}

const FullPageImageCarousel: React.FC<FullPageImageCarouselProps> = ({
  images,
  initialIndex,
  onClose,
  activateWatermark,
  watermarkUrl,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [loaded, setLoaded] = useState(false)
  const current = images[currentIndex]

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => (i <= 0 ? i : i - 1))
    setLoaded(false)
  }, [])

  const goNext = useCallback(() => {
    setCurrentIndex((i) => (i >= images.length - 1 ? i : i + 1))
    setLoaded(false)
  }, [images.length])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') goPrev()
      if (e.key === 'ArrowRight') goNext()
    },
    [onClose, goPrev, goNext]
  )

  useEffect(() => {
    setCurrentIndex(Math.min(initialIndex, images.length - 1))
  }, [initialIndex, images.length])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [handleKeyDown])

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }

  const displayUrl = current?.url
  const thumbnailUrl = current?.thumbnail

  if (!images.length) return null

  return (
    <Overlay onClick={handleBackdropClick} role="dialog" aria-modal="true" aria-label="Image carousel">
      <MainContent onClick={handleBackdropClick}>
        <CloseButton type="button" onClick={onClose} aria-label="Close">
          <span>×</span>
        </CloseButton>

        <NavButton
          type="button"
          side="left"
          onClick={(e) => {
            e.stopPropagation()
            goPrev()
          }}
          disabled={currentIndex <= 0}
          aria-label="Previous image"
        >
          <span>‹</span>
        </NavButton>

        <ImageWrapper>
          <ImageFrame onClick={(e) => e.stopPropagation()}>
            <FullPageImage
              src={loaded ? displayUrl : (thumbnailUrl || displayUrl || PreviewImagePNG)}
              alt={`Image ${currentIndex + 1} of ${images.length}`}
              onLoad={() => setLoaded(true)}
              draggable={false}
            />
            {activateWatermark && watermarkUrl && (
              <WatermarkOverlay
                src={watermarkUrl}
                alt="Watermark"
              />
            )}
          </ImageFrame>
        </ImageWrapper>

        <NavButton
          type="button"
          side="right"
          onClick={(e) => {
            e.stopPropagation()
            goNext()
          }}
          disabled={currentIndex >= images.length - 1}
          aria-label="Next image"
        >
          <span>›</span>
        </NavButton>

        <Counter>
          {currentIndex + 1} / {images.length}
        </Counter>
      </MainContent>
    </Overlay>
  )
}

export default FullPageImageCarousel
